import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquareIcon, XIcon, BotIcon, UserIcon, RefreshCwIcon, ArrowRightIcon, HomeIcon, ShowerHeadIcon, CookingPotIcon, ThermometerIcon, FootprintsIcon, TreesIcon } from 'lucide-react';
interface AIAdvisorProps {
  onNavigateToQuote: (serviceSlug?: string) => void;
}
type Message = {
  id: string;
  role: 'assistant' | 'user';
  content: React.ReactNode;
};
// Data structure for the guided flow
const CATEGORIES = [{
  id: 'bathroom',
  label: 'Bathroom',
  icon: <ShowerHeadIcon className="w-4 h-4" data-id="element-149" />
}, {
  id: 'kitchen',
  label: 'Kitchen',
  icon: <CookingPotIcon className="w-4 h-4" data-id="element-150" />
}, {
  id: 'exterior',
  label: 'Exterior',
  icon: <HomeIcon className="w-4 h-4" data-id="element-151" />
}, {
  id: 'systems',
  label: 'Systems',
  icon: <ThermometerIcon className="w-4 h-4" data-id="element-152" />
}, {
  id: 'interior',
  label: 'Interior',
  icon: <FootprintsIcon className="w-4 h-4" data-id="element-153" />
}, {
  id: 'outdoor',
  label: 'Outdoor & Other',
  icon: <TreesIcon className="w-4 h-4" data-id="element-154" />
}];
const SERVICES_BY_CATEGORY: Record<string, {
  label: string;
  slug: string;
  desc: string;
  cost: string;
}[]> = {
  bathroom: [{
    label: 'Walk-In Showers',
    slug: 'walk-in-showers',
    desc: 'Safe, accessible walk-in showers.',
    cost: '$2,500 - $8,000'
  }, {
    label: 'Walk-In Tubs',
    slug: 'walk-in-tubs',
    desc: 'Therapeutic, accessible tubs.',
    cost: '$3,000 - $10,000'
  }, {
    label: 'Bathroom Remodel',
    slug: 'bathroom-remodel',
    desc: 'Complete bathroom renovation.',
    cost: '$10,000 - $30,000'
  }],
  kitchen: [{
    label: 'Kitchen Remodel',
    slug: 'kitchen-remodel',
    desc: 'Stunning new kitchen design.',
    cost: '$15,000 - $50,000'
  }],
  exterior: [{
    label: 'Roofing',
    slug: 'roofing',
    desc: 'Durable, weather-resistant roofs.',
    cost: '$5,000 - $15,000'
  }, {
    label: 'Window Replacement',
    slug: 'window-replacement',
    desc: 'Energy efficient windows.',
    cost: '$400 - $1,500 per window'
  }, {
    label: 'Siding',
    slug: 'siding',
    desc: 'Low-maintenance exterior siding.',
    cost: '$6,000 - $18,000'
  }, {
    label: 'Gutter Guards',
    slug: 'gutter-guards',
    desc: 'Protect from water damage.',
    cost: '$900 - $3,000'
  }, {
    label: 'Doors',
    slug: 'doors',
    desc: 'Secure exterior doors.',
    cost: '$800 - $3,000'
  }, {
    label: 'Garage Doors',
    slug: 'garage-doors',
    desc: 'Secure garage doors.',
    cost: '$1,000 - $3,000'
  }, {
    label: 'Fencing',
    slug: 'fencing',
    desc: 'Privacy, security, and style.',
    cost: '$2,000 - $6,000'
  }, {
    label: 'Painting',
    slug: 'painting',
    desc: 'Interior or exterior painting.',
    cost: '$1,000 - $5,000'
  }],
  systems: [{
    label: 'HVAC',
    slug: 'hvac',
    desc: 'Heating and cooling systems.',
    cost: '$5,000 - $12,000'
  }, {
    label: 'Plumbing',
    slug: 'plumbing',
    desc: 'Resolve leaks, clogs, and upgrades.',
    cost: '$150 - $1,500'
  }, {
    label: 'Electrical',
    slug: 'electrical',
    desc: 'Safe and fully powered electrical work.',
    cost: '$200 - $2,000'
  }, {
    label: 'Solar Panels',
    slug: 'solar',
    desc: 'Clean energy and lower bills.',
    cost: '$15,000 - $30,000'
  }, {
    label: 'Insulation',
    slug: 'insulation',
    desc: 'Keep home warm in winter, cool in summer.',
    cost: '$1,000 - $3,000'
  }, {
    label: 'Home Security',
    slug: 'home-security',
    desc: 'Modern home security systems.',
    cost: '$300 - $1,500'
  }],
  interior: [{
    label: 'Flooring',
    slug: 'flooring',
    desc: 'Beautiful, durable new flooring.',
    cost: '$1,500 - $6,000'
  }, {
    label: 'Stairlifts',
    slug: 'stairlifts',
    desc: 'Safe navigation between floors.',
    cost: '$2,000 - $6,000'
  }],
  outdoor: [{
    label: 'Landscaping',
    slug: 'landscaping',
    desc: 'Beautiful outdoor living spaces.',
    cost: '$1,500 - $10,000'
  }, {
    label: 'Pest Control',
    slug: 'pest-control',
    desc: 'Eliminate unwanted guests.',
    cost: '$150 - $500'
  }, {
    label: 'Cleaning',
    slug: 'cleaning',
    desc: 'Professional deep cleaning.',
    cost: '$150 - $400'
  }, {
    label: 'General Repair',
    slug: 'general',
    desc: 'Skilled local handyman.',
    cost: '$100 - $600'
  }]
};
export function AIAdvisor({
  onNavigateToQuote
}: AIAdvisorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [currentStep, setCurrentStep] = useState<'category' | 'service' | 'done'>('category');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen, isTyping]);
  // Initial greeting
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        id: 'greeting',
        role: 'assistant',
        content: "Hi there! I'm your Home Advisor. What area of your home needs work?"
      }]);
    }
  }, [messages.length]);
  const handleCategorySelect = (categoryId: string, categoryLabel: string) => {
    // Add user message
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      role: 'user',
      content: categoryLabel
    }]);
    setSelectedCategory(categoryId);
    setCurrentStep('service');
    setIsTyping(true);
    // Simulate typing delay
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'assistant',
        content: `Great! What specific ${categoryLabel.toLowerCase()} service are you looking for?`
      }]);
    }, 600);
  };
  const handleServiceSelect = (service: {
    label: string;
    slug: string;
    desc: string;
    cost: string;
  }) => {
    // Add user message
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      role: 'user',
      content: service.label
    }]);
    setCurrentStep('done');
    setIsTyping(true);
    // Simulate typing delay
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        role: 'assistant',
        content: <div className="space-y-3" data-id="element-155">
              <p data-id="element-156">
                Excellent choice! <strong data-id="element-157">{service.label}</strong> is a great
                investment.
              </p>
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 text-sm" data-id="element-158">
                <p className="text-slate-600 mb-1" data-id="element-159">{service.desc}</p>
                <p className="font-semibold text-slate-800" data-id="element-160">
                  Est. Cost: {service.cost}
                </p>
              </div>
              <p data-id="element-161">
                I highly recommend getting a few free quotes from local
                professionals to get an exact price for your home.
              </p>
              <button onClick={() => onNavigateToQuote(service.slug)} className="mt-2 w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-xl transition-colors shadow-sm" data-id="element-162">
                Get Free Quotes <ArrowRightIcon className="w-4 h-4" data-id="element-163" />
              </button>
            </div>
      }]);
    }, 800);
  };
  const resetFlow = () => {
    setMessages([{
      id: Date.now().toString(),
      role: 'assistant',
      content: "Hi there! I'm your Home Advisor. What area of your home needs work?"
    }]);
    setCurrentStep('category');
    setSelectedCategory(null);
  };
  return <>
      {/* Floating Action Button */}
      <AnimatePresence data-id="element-164">
        {!isOpen && <motion.button initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0,
        opacity: 0
      }} whileHover={{
        scale: 1.05
      }} whileTap={{
        scale: 0.95
      }} onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-slate-900 text-white rounded-full shadow-2xl flex items-center justify-center hover:bg-slate-800 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2" aria-label="Open Home Advisor" data-id="element-165">
            <div className="relative" data-id="element-166">
              <MessageSquareIcon className="w-6 h-6" data-id="element-167" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full border-2 border-slate-900" data-id="element-168"></span>
            </div>
          </motion.button>}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence data-id="element-169">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[600px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden" data-id="element-170">
            {/* Header */}
            <div className="bg-slate-900 px-4 py-3 flex items-center justify-between flex-shrink-0" data-id="element-171">
              <div className="flex items-center gap-3" data-id="element-172">
                <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white" data-id="element-173">
                  <BotIcon className="w-5 h-5" data-id="element-174" />
                </div>
                <div data-id="element-175">
                  <h3 className="text-white font-bold text-sm" data-id="element-176">Home Advisor</h3>
                  <p className="text-slate-400 text-xs" data-id="element-177">
                    Guided Service Finder
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1" data-id="element-178">
                <button onClick={resetFlow} className="p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-800" title="Start Over" data-id="element-179">
                  <RefreshCwIcon className="w-4 h-4" data-id="element-180" />
                </button>
                <button onClick={() => setIsOpen(false)} className="p-2 text-slate-400 hover:text-white transition-colors rounded-lg hover:bg-slate-800" title="Close" data-id="element-181">
                  <XIcon className="w-5 h-5" data-id="element-182" />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 bg-slate-50 space-y-4" data-id="element-183">
              {messages.map(msg => <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-184">
                  {msg.role === 'assistant' && <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-185">
                      <BotIcon className="w-4 h-4 text-amber-600" data-id="element-186" />
                    </div>}
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${msg.role === 'user' ? 'bg-amber-500 text-white rounded-tr-sm font-medium' : 'bg-white border border-slate-200 text-slate-700 rounded-tl-sm shadow-sm'}`} data-id="element-187">
                    {msg.content}
                  </div>
                  {msg.role === 'user' && <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-188">
                      <UserIcon className="w-4 h-4 text-slate-500" data-id="element-189" />
                    </div>}
                </motion.div>)}

              {isTyping && <div className="flex gap-3 justify-start" data-id="element-190">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-1" data-id="element-191">
                    <BotIcon className="w-4 h-4 text-amber-600" data-id="element-192" />
                  </div>
                  <div className="bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-4 py-4 shadow-sm flex items-center gap-1" data-id="element-193">
                    <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" data-id="element-194" />
                    <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{
                animationDelay: '0.2s'
              }} data-id="element-195" />
                    <div className="w-2 h-2 bg-slate-300 rounded-full animate-bounce" style={{
                animationDelay: '0.4s'
              }} data-id="element-196" />
                  </div>
                </div>}

              {/* Interactive Options Area */}
              {!isTyping && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="pl-11 pr-2 pt-2" data-id="element-197">
                  {currentStep === 'category' && <div className="flex flex-wrap gap-2" data-id="element-198">
                      {CATEGORIES.map(cat => <button key={cat.id} onClick={() => handleCategorySelect(cat.id, cat.label)} className="flex items-center gap-1.5 px-3 py-2 bg-white border border-amber-200 text-amber-700 rounded-xl text-sm font-medium hover:bg-amber-50 hover:border-amber-300 transition-colors shadow-sm" data-id="element-199">
                          {cat.icon}
                          {cat.label}
                        </button>)}
                    </div>}

                  {currentStep === 'service' && selectedCategory && SERVICES_BY_CATEGORY[selectedCategory] && <div className="flex flex-col gap-2" data-id="element-200">
                        {SERVICES_BY_CATEGORY[selectedCategory].map(service => <button key={service.slug} onClick={() => handleServiceSelect(service)} className="text-left px-4 py-3 bg-white border border-amber-200 text-amber-800 rounded-xl text-sm font-medium hover:bg-amber-50 hover:border-amber-300 transition-colors shadow-sm flex items-center justify-between group" data-id="element-201">
                              {service.label}
                              <ArrowRightIcon className="w-4 h-4 text-amber-400 group-hover:text-amber-600 transition-colors" data-id="element-202" />
                            </button>)}
                        <button onClick={() => {
                setCurrentStep('category');
                setSelectedCategory(null);
              }} className="text-center px-4 py-2 mt-2 text-slate-500 text-xs font-medium hover:text-slate-700 transition-colors" data-id="element-203">
                          &larr; Back to categories
                        </button>
                      </div>}
                </motion.div>}

              <div ref={messagesEndRef} data-id="element-204" />
            </div>

            {/* Footer Area */}
            <div className="p-4 bg-white border-t border-slate-200 flex-shrink-0" data-id="element-205">
              <div className="text-center" data-id="element-206">
                <button type="button" onClick={() => onNavigateToQuote()} className="text-xs text-slate-500 hover:text-amber-600 transition-colors font-medium flex items-center justify-center gap-1 w-full" data-id="element-207">
                  Skip and go to general quote form{' '}
                  <ArrowRightIcon className="w-3 h-3" data-id="element-208" />
                </button>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}